import React from 'react';
import { Alert, Box, Button, Typography } from '@mui/material';

interface State { error: Error | null; }

/**
 * Catches render-time errors in any subtree and shows a recoverable panel
 * instead of blanking the whole SPA. Wrap suspicious pages with this.
 */
export class ErrorBoundary extends React.Component<{ children: React.ReactNode }, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State { return { error }; }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // Surface to console so DevTools shows the stack.
    // eslint-disable-next-line no-console
    console.error('Render error:', error, info);
  }

  render() {
    if (!this.state.error) return this.props.children;
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Something went wrong rendering this page</Typography>
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            {this.state.error.message}
          </Typography>
        </Alert>
        <Box component="pre" sx={{ fontSize: 11, maxHeight: 300, overflow: 'auto', bgcolor: 'background.default', p: 2, borderRadius: 1 }}>
          {this.state.error.stack}
        </Box>
        <Button variant="contained" sx={{ mt: 2 }} onClick={() => this.setState({ error: null })}>
          Try again
        </Button>
      </Box>
    );
  }
}
